import { createFeature, createReducer, on } from '@ngrx/store'
import { caseActions } from './case.actions'
import { CaseState } from './case.state'
import { computeSharedPropertyNames } from '../../shared/utils/property-catalog-utils'

const createInitialState = (): CaseState => ({
    caseId: null,
    caseName: null,
    isSharedCase: false,
    sharedPropertyNames: [],
    caseSegObjectPropertyMap: undefined,
    caseSegObjectPropertyMaps: [],
    loadedSegObjectPropertyMaps: [],
    propertyCatalogByMapId: {},
    propertyNamesByMapId: {},
    markerDimensionsByMapId: {},
    caseRcnResults: [],
    caseNeighborhoodEnrichmentResults: [],
    caseTsneResults: [],
    caseUmapResults: [],
    caseCellTypeDetectionResults: [],
    casePlottingResults: [],
})

export const caseFeature = createFeature({
    name: 'case',
    reducer: createReducer<CaseState>(
        createInitialState(),
        on(caseActions.loadCase, (state, { caseId, caseName, shared }): CaseState => {
            return {
                ...state,
                caseId: caseId,
                caseName: caseName,
                isSharedCase: shared,
                sharedPropertyNames: [],
                caseSegObjectPropertyMap: undefined,
                caseSegObjectPropertyMaps: [],
                loadedSegObjectPropertyMaps: [],
                propertyCatalogByMapId: {},
                propertyNamesByMapId: {},
                markerDimensionsByMapId: {},
                caseRcnResults: [],
                caseNeighborhoodEnrichmentResults: [],
                caseTsneResults: [],
                caseUmapResults: [],
                caseCellTypeDetectionResults: [],
                casePlottingResults: [],
            }
        }),

        on(caseActions.loadingSegObjectPropertiesDataSucceeded, (state, action): CaseState => {
            const mapId = action.segobjectpropertymap.id
            const markersWithMap = action.segobjectpropertiesdata.map(marker => ({
                ...marker,
                segobjectpropertymap: marker.segobjectpropertymap ?? action.segobjectpropertymap,
            }))
            const catalog = markersWithMap
                .map(({ name, lower_bound, upper_bound }) => ({
                    name,
                    lower_bound,
                    upper_bound,
                }))
                .sort((a, b) => a.name.localeCompare(b.name))
            const names = catalog.map(p => p.name)
            const propertyCatalogByMapId =
                mapId != null
                    ? { ...state.propertyCatalogByMapId, [mapId]: catalog }
                    : state.propertyCatalogByMapId
            const propertyNamesByMapId =
                mapId != null
                    ? { ...state.propertyNamesByMapId, [mapId]: names }
                    : state.propertyNamesByMapId
            const markerDimensionsByMapId =
                mapId != null
                    ? { ...state.markerDimensionsByMapId, [mapId]: markersWithMap }
                    : state.markerDimensionsByMapId
            return {
                ...state,
                propertyCatalogByMapId,
                propertyNamesByMapId,
                markerDimensionsByMapId,
                sharedPropertyNames: computeSharedPropertyNames(
                    propertyNamesByMapId,
                    state.loadedSegObjectPropertyMaps
                ),
            }
        }),

        on(caseActions.loadingSegObjectPropertyMapsSucceeded, (state, action): CaseState => {
            return {
                ...state,
                caseSegObjectPropertyMaps: action.segobjectpropertymaps,
            }
        }),
        on(
            caseActions.loadingRecurrentNeighborhoodsResultsSucceeded,
            (state, { neighborhood }): CaseState => {
                return {
                    ...state,
                    caseRcnResults: neighborhood,
                }
            }
        ),
        on(
            caseActions.loadingNeighborhoodEnrichmentResultsSucceeded,
            (state, { neighborhood }): CaseState => {
                return {
                    ...state,
                    caseNeighborhoodEnrichmentResults: neighborhood,
                }
            }
        ),
        on(caseActions.loadingTsneResultsSucceeded, (state, { tsne }): CaseState => {
            return {
                ...state,
                caseTsneResults: tsne,
            }
        }),
        on(caseActions.loadingUmapResultsSucceeded, (state, { umap }): CaseState => {
            return {
                ...state,
                caseUmapResults: umap,
            }
        }),
        on(
            caseActions.loadingAutoCellDetectionResultsSucceeded,
            (state, { autoCellDetection }): CaseState => {
                return {
                    ...state,
                    caseCellTypeDetectionResults: autoCellDetection,
                }
            }
        ),
        on(caseActions.loadingPlottingResultsSucceeded, (state, { plotting }): CaseState => {
            return {
                ...state,
                casePlottingResults: plotting,
            }
        }),

        on(caseActions.loadSegObjectPropertiesData, (state, action): CaseState => {
            return {
                ...state,
                caseSegObjectPropertyMap: action.segobjectpropertymap,
            }
        }),
        on(caseActions.toggleLoadedSegObjectPropertyMap, (state, { segobjectpropertymap }): CaseState => {
            const isLoaded = state.loadedSegObjectPropertyMaps.some(m => m.id === segobjectpropertymap.id)
            if (
                !isLoaded &&
                segobjectpropertymap.segSource &&
                state.loadedSegObjectPropertyMaps.some(
                    m =>
                        m.id !== segobjectpropertymap.id &&
                        m.segSource === segobjectpropertymap.segSource
                )
            ) {
                // Only one intensity map per segmentation (segSource) at a time.
                return state
            }
            const loadedSegObjectPropertyMaps = isLoaded
                ? state.loadedSegObjectPropertyMaps.filter(m => m.id !== segobjectpropertymap.id)
                : [...state.loadedSegObjectPropertyMaps, segobjectpropertymap]
            const propertyCatalogByMapId = { ...state.propertyCatalogByMapId }
            const propertyNamesByMapId = { ...state.propertyNamesByMapId }
            const markerDimensionsByMapId = { ...state.markerDimensionsByMapId }
            if (isLoaded) {
                delete propertyCatalogByMapId[segobjectpropertymap.id]
                delete propertyNamesByMapId[segobjectpropertymap.id]
                delete markerDimensionsByMapId[segobjectpropertymap.id]
            }
            const caseSegObjectPropertyMap = isLoaded
                ? loadedSegObjectPropertyMaps[loadedSegObjectPropertyMaps.length - 1]
                : segobjectpropertymap
            return {
                ...state,
                loadedSegObjectPropertyMaps,
                propertyCatalogByMapId,
                propertyNamesByMapId,
                markerDimensionsByMapId,
                caseSegObjectPropertyMap,
                sharedPropertyNames: computeSharedPropertyNames(
                    propertyNamesByMapId,
                    loadedSegObjectPropertyMaps
                ),
            }
        }),
        on(caseActions.clearSegObjectPropertyMapSelection, (state): CaseState => ({
            ...state,
            caseSegObjectPropertyMap: undefined,
            loadedSegObjectPropertyMaps: [],
            propertyCatalogByMapId: {},
            propertyNamesByMapId: {},
            markerDimensionsByMapId: {},
            sharedPropertyNames: [],
        }))
    ),
})

export const { reducer } = caseFeature
